import { useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type MasterCandidate, type MasterRowAddress } from '../lib/api';

type AddForm = { address: string; city: string; state: string; zip: string };

/**
 * AIW2 - "Is this ...? Link". Shown on a My Deals row and its CRM deal while the row has no Property Database record.
 * ⛔ A refusal he cannot see is a dead button (the 111th): a failed Link or Add says why, beside it.
 * AIW2-ADD (the 119th, spec D7): with no candidate it offers "Add to the Property Database" - a form on the ROW's own
 * address for him to confirm or correct. The server asks first: when that address finds a record, the prompt turns into
 * the same "Is this ...? Link", and Link carries the address he confirmed.
 * AIW2-NONE (the 120th, his YES): under any "Is this ...?" - "None of these - add it anyway". An APN on a parcel holding
 * several buildings can name the wrong one, and Link was the only way on. After Add asked, it re-sends that address with
 * his press; on a match found before any Add, it opens the form, whose Add carries the press. The server still refuses a
 * record at exactly this address - said beside the button.
 */
export default function MasterLinkPrompt({ propertyId, onLinked }: { propertyId: string; onLinked?: () => void }) {
  const [state, setState] = useState<{ linked: boolean; candidates: MasterCandidate[]; row: MasterRowAddress | null } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<AddForm | null>(null);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState<{ address: string; city: string; state: string } | null>(null);
  // AIW2-NONE: the address Add sent when the server asked instead of creating - "add it anyway" re-sends exactly that.
  const [asked, setAsked] = useState<AddForm | null>(null);
  const ids = useId();

  useEffect(() => {
    let live = true;
    // A side prompt never takes the page down: a call that THROWS is treated like one that fails - nothing is shown.
    Promise.resolve()
      .then(() => api.getMasterCandidates(propertyId))
      .then((r) => { if (live) setState({ linked: !!r.linked_master_id, candidates: r.candidates ?? [], row: r.row ?? null }); })
      .catch(() => { if (live) setState(null); });
    return () => { live = false; };
  }, [propertyId]);

  if (!state || state.linked) return null;

  const link = async (id: string) => {
    setBusy(id); setError(null);
    try {
      if (confirmed) await api.linkPropertyMaster(propertyId, id, confirmed);
      else await api.linkPropertyMaster(propertyId, id);
      setState({ ...state, linked: true, candidates: [] });
      onLinked?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not link the record');
    } finally { setBusy(null); }
  };

  const openForm = () => setForm({
    address: state.row?.address ?? '', city: state.row?.city ?? '', state: state.row?.state ?? 'CA', zip: state.row?.zip ?? '',
  });

  const add = async (f: AddForm, anyway = false) => {
    const body = { address: f.address.trim(), city: f.city.trim(), state: f.state.trim().toUpperCase(), zip: f.zip.trim() || null };
    // The review (the 119th): the server's refusal arrived as "Validation failed" with no reason - the form says it.
    const wrong = !body.address || !body.city ? 'Address and city are required'
      : !/^[A-Z]{2}$/.test(body.state) ? 'State must be two letters, like CA'
      : body.zip && !/^\d{5}(-\d{4})?$/.test(body.zip) ? 'ZIP must be 5 digits, or ZIP+4'
      : null;
    if (wrong) { setAddError(wrong); return; }
    setAdding(true); setAddError(null);
    try {
      // The review (I1): his press covers only the records ON SCREEN - the server asks about any other it finds.
      const r = await api.addPropertyMaster(propertyId, anyway ? { ...body, anyway: true, passed_over: state.candidates.map((c) => c.id) } : body);
      if (r.linked_master_id) {
        setState({ ...state, linked: true, candidates: [] });
        onLinked?.();
      } else {
        // The address he confirmed found a record: ask, exactly as a match does - and Link carries that address.
        setConfirmed({ address: body.address, city: body.city, state: body.state });
        setAsked({ address: body.address, city: body.city, state: body.state, zip: body.zip ?? '' });
        setForm(null);
        setState({ ...state, candidates: r.candidates ?? [] });
      }
    } catch (e) {
      setAddError(e instanceof Error ? e.message : 'Could not add the record');
    } finally { setAdding(false); }
  };

  const field = (key: keyof AddForm, label: string, width: string) => (
    <label htmlFor={`${ids}-${key}`} className={`flex flex-col gap-1 ${width}`}>
      <span className="text-xs text-gray-600">{label}</span>
      <input id={`${ids}-${key}`} value={form?.[key] ?? ''} onChange={(e) => setForm((cur) => (cur ? { ...cur, [key]: e.target.value } : cur))}
        className="rounded border border-gray-300 px-2 py-1 text-sm" />
    </label>
  );

  const formView = (f: AddForm, anyway: boolean) => (
    <form onSubmit={(e) => { e.preventDefault(); void add(f, anyway); }} className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {field('address', 'Address', 'min-w-[12rem] flex-1')}
        {field('city', 'City', 'w-36')}
        {field('state', 'State', 'w-16')}
        {field('zip', 'ZIP', 'w-24')}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button type="submit" disabled={adding} className="rounded bg-primary-600 px-3 py-1 text-white disabled:opacity-50">Add</button>
        <button type="button" disabled={adding} onClick={() => { setForm(null); setAddError(null); }} className="rounded border border-gray-300 px-3 py-1">Cancel</button>
        {addError && <p className="text-primary-500">{addError}</p>}
      </div>
    </form>
  );

  // AIW2-NONE: after Add asked, re-send that address with his press; before any Add, open the form on the row's address.
  const addAnyway = () => { if (asked) void add(asked, true); else openForm(); };

  return (
    <div className="rounded-lg border border-warning bg-warning-wash px-4 py-3 text-sm">
      {state.candidates.length === 0 ? (
        <div className="space-y-2">
          <p>
            <span>No Property Database record matches this address.</span>{' '}
            <Link to="/data-hub" className="text-primary-600 underline">Search the Property Database</Link>
          </p>
          {form === null ? (
            <button type="button" onClick={openForm} className="rounded bg-primary-600 px-3 py-1 text-white">Add to the Property Database</button>
          ) : (
            formView(form, false)
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <ul className="space-y-2">
            {state.candidates.map((c) => (
              <li key={c.id} className="flex flex-wrap items-center gap-3">
                <span>{`Is this ${[c.address, c.city].filter(Boolean).join(', ')}${c.apn ? ` (APN ${c.apn})` : ''}?`}</span>
                <button type="button" disabled={busy !== null || adding} onClick={() => link(c.id)}
                  className="rounded bg-primary-600 px-3 py-1 text-white disabled:opacity-50">Link</button>
              </li>
            ))}
          </ul>
          {form !== null ? formView(form, true) : (
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" disabled={adding || busy !== null} onClick={addAnyway}
                className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50">None of these - add it anyway</button>
              {addError && <p className="text-primary-500">{addError}</p>}
            </div>
          )}
        </div>
      )}
      {error && <p className="mt-2 text-primary-500">{error}</p>}
    </div>
  );
}
